import { z } from 'zod';
import { generateStructuredObject } from './structured.js';
import { validatePrompt } from './input-validation.js';

// Intent Wizard LLM calls. Each resolves to the same payload shape the retired
// server WebSocket handlers sent ('wizard:questions-batch', 'wizard:final',
// 'wizard:question'), so the wizard UI consumes them unchanged.

// Handle batch wizard questions - generates all 4 questions at once
export async function getWizardQuestionsBatch(initialPrompt, options = {}) {
  const { promptStylePreference } = options;

  const stylePreference = typeof promptStylePreference === 'string'
    ? promptStylePreference.trim()
    : '';

  if (initialPrompt) {
    validatePrompt(initialPrompt);
  }

  try {
    console.log(`🧙 Wizard BATCH request - generating all 4 questions at once`);

    const presetSchema = z.object({
      title: z.string().describe('Short 2-3 word summary title for the option'),
      description: z.string().describe('Concise descriptive answer, about 6–12 words')
    });

    const questionSchema = z.object({
      question: z.string().describe('The question to ask the user'),
      fieldName: z.string().describe('Internal field name for this data'),
      pillar: z.enum(['subject_action', 'environment_mood', 'composition_camera', 'style']).describe('Which of the four pillars this question addresses'),
      pillarStatus: z.enum(['missing', 'partial', 'refinement']).describe('Whether this pillar was missing, partial, or being refined'),
      presets: z.array(presetSchema).length(6).describe('6 preset options with title summaries'),
      importance: z.enum(['critical', 'important', 'optional']).describe('How important this field is')
    });

    const batchSchema = z.object({
      analysis: z.object({
        subject_action: z.enum(['missing', 'partial', 'present']).describe('Status of subject & action in the prompt'),
        environment_mood: z.enum(['missing', 'partial', 'present']).describe('Status of environment & mood in the prompt'),
        composition_camera: z.enum(['missing', 'partial', 'present']).describe('Status of composition & camera in the prompt'),
        style: z.enum(['missing', 'partial', 'present']).describe('Status of style in the prompt')
      }).describe('Analysis of which pillars are present in the initial prompt'),
      questions: z.array(questionSchema).length(4).describe('Exactly 4 questions filling in the gaps')
    });

    let systemPrompt = `You are a creative assistant helping users refine their image generation ideas.
Your job is to analyze what the user has already provided and ask questions to fill in the gaps.

THE FOUR PILLARS OF A GREAT IMAGE:
1. SUBJECT & ACTION - Who/what is in the image and what are they doing
2. ENVIRONMENT & MOOD - Where it takes place and the emotional atmosphere
3. COMPOSITION & CAMERA - How it's framed (close-up, wide shot, angle, perspective)
4. STYLE - Artistic medium and visual treatment (photorealistic, watercolor, anime, etc.)

CRITICAL PROCESS:
1. First, ANALYZE the user's prompt to identify which pillars are already present:
   - Subject present? (person, animal, object, scene element)
   - Action present? (what they're doing, movement, pose)
   - Environment present? (location, setting, background)
   - Mood present? (emotional words, atmosphere descriptors)
   - Composition/Camera present? (framing, angle, perspective mentioned)
   - Style present? (art style, medium, aesthetic mentioned)

2. Generate exactly 4 questions that FILL IN THE GAPS:
   - If a pillar is MISSING entirely → ask about it directly
   - If a pillar is PARTIAL → ask to expand/clarify it
   - If a pillar is COMPLETE → ask a refinement question that adds depth

3. Prioritize questions for MISSING pillars over refining present ones

QUESTION GUIDELINES:
- CRITICAL: Reference the user's ACTUAL subject from their prompt in your questions. Do NOT use generic placeholders or examples - use what THEY described.
- Keep questions under about 15 words - no examples or introductions
- IMPORTANT: Each question must ask about ONE SPECIFIC THING only. Never combine multiple questions into one (e.g., don't ask "What age is she and what is she doing?" - pick ONE aspect to ask about).
- Each preset needs a short title (2-3 words) and a concise description (about 6-12 words)
- CRITICAL: Presets must DIRECTLY ANSWER the question being asked. If you ask about age, presets must be age options. If you ask about interaction style, presets must be interaction options. Never provide presets that answer a different question than what was asked.
- Presets should offer meaningfully different creative directions

PRESET FORMAT:
- Title: Brief, catchy summary (2-3 words)
- Description: One concise answer, about 6-12 words; no explanatory paragraph`;

    let userPrompt = `User's creative vision: "${initialPrompt}"

First, analyze this prompt against the four pillars:
- SUBJECT & ACTION: What's present? What's missing?
- ENVIRONMENT & MOOD: What's present? What's missing?
- COMPOSITION & CAMERA: What's present? What's missing?
- STYLE: What's present? What's missing?

Then generate 4 questions that fill in the most important gaps to create an amazing image.
Prioritize asking about completely missing pillars before refining partial ones.`;

    if (stylePreference) {
      systemPrompt += `\n\nSTYLE PRIORITY: The user has a strong stylistic preference:\n${stylePreference}\nEnsure questions and presets respect and reinforce this style guidance.`;
      userPrompt += `\n\nStyle preference to emphasize: ${stylePreference}`;
    }

    console.log('🔵 Batch wizard question generation request');
    const startTime = Date.now();

    const object = await generateStructuredObject({
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
      ],
      schema: batchSchema,
      temperature: 0.8,
      // 4 questions + presets exceed the 1600-token structured floor on qwen3.6,
      // which burns tokens on thinking before the tool call (finishReason=length).
      maxTokens: 6000
    });

    const responseTime = Date.now() - startTime;
    console.log(`✅ Batch questions generated in ${responseTime}ms`);
    console.log(`📊 Pillar Analysis:`, object.analysis);
    console.log(`❓ Questions: ${object.questions.map(q => `${q.pillar}(${q.pillarStatus})`).join(', ')}`);

    return {
      type: 'wizard:questions-batch',
      analysis: object.analysis,
      questions: object.questions
    };

  } catch (error) {
    console.error('Wizard batch error:', error);
    throw error;
  }
}

export async function getWizardQuestion(initialPrompt, fields = [], readyToGenerate = false, options = {}) {
  const {
    refreshOptions = false,
    seed = 0,
    previousPresets = [],
    currentQuestionContext = null,
    promptStylePreference
  } = options;

  const stylePreference = typeof promptStylePreference === 'string'
    ? promptStylePreference.trim()
    : '';

  if (initialPrompt) {
    validatePrompt(initialPrompt);
  }

  try {
    console.log(`🧙 Wizard request - Ready: ${readyToGenerate}, Fields: ${fields.length}`);

    if (readyToGenerate) {
      // Generate final prompt from collected fields
      const consolidationSchema = z.object({
        finalPrompt: z.string().describe('The complete, detailed image generation prompt'),
        summary: z.string().optional().describe('Brief summary of the creative vision')
      });

      let systemPrompt = `You are an expert at creating detailed image generation prompts from creative briefs.
Consolidate the user's initial idea and all collected details into one comprehensive prompt for Flux.1 model.

THE FOUR PILLARS - ensure all are represented in the final prompt:
1. SUBJECT & ACTION - Who/what is shown and what they're doing
2. ENVIRONMENT & MOOD - The setting and emotional atmosphere
3. COMPOSITION & CAMERA - How it's framed and the perspective
4. STYLE - The artistic medium and visual treatment

The prompt should be rich, visual, and specific while staying under 300 words.
CRITICAL: Output ONLY the direct image description prompt itself. DO NOT start with phrases like "Create an image of" or "Generate an image showing". Just describe what should be in the image directly.`;

      let userPrompt = `Synthesize this creative brief into a direct, descriptive image generation prompt:

Initial idea: "${initialPrompt}"

Details collected (organized by pillar):
${fields.map(f => `- ${f.name}: ${f.value}`).join('\n')}

Weave all four pillars together into a cohesive visual description. Ensure no pillar is neglected.
Output the final prompt as a direct visual description (not instructions to create something).`;

      if (stylePreference) {
        systemPrompt += `\n\nSTYLE PRIORITY: The user insists on the following style characteristics:\n${stylePreference}\nThe final prompt must clearly embody this style guidance.`;
        userPrompt += `\n\nStyle preference to emphasize: ${stylePreference}`;
      }

      console.log('🔵 Final prompt consolidation request');
      const startTime = Date.now();

      const object = await generateStructuredObject({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        schema: consolidationSchema,
        temperature: 0.7
      });

      const responseTime = Date.now() - startTime;
      console.log(`✅ Final prompt generated in ${responseTime}ms`);

      return {
        type: 'wizard:final',
        result: object
      };

    } else if (refreshOptions && currentQuestionContext) {
      // Refresh the same question topic with new presets and concise wording.
      console.log(`🔄 Refreshing presets and question for pillar: "${currentQuestionContext.pillar}"`);

      const presetSchema = z.object({
        title: z.string().describe('Short 2-3 word summary title for the option'),
        description: z.string().describe('Concise descriptive answer, about 6–12 words')
      });

      const refreshSchema = z.object({
        question: z.string().describe('The same question topic, concise and without examples'),
        presets: z.array(presetSchema).length(6).describe('6 NEW preset options with title summaries')
      });

      const normalizedPreviousPresets = Array.isArray(previousPresets)
        ? previousPresets.filter(Boolean)
        : [];

      // Format previous presets for context
      const formatPreset = (preset, index) => {
        if (typeof preset === 'object' && preset.description) {
          return `${index + 1}. ${preset.title || ''}: ${preset.description}`;
        }
        return `${index + 1}. ${preset}`;
      };

      const previousPresetList = normalizedPreviousPresets.length
        ? normalizedPreviousPresets.map(formatPreset).join('\n')
        : 'None';

      const systemPrompt = `You are a creative assistant generating questions and preset answer options for image generation.

The user is answering questions to refine their creative vision. They've seen the current options and want DIFFERENT ones.

IMPORTANT:
1. Generate 6 COMPLETELY NEW and DIFFERENT preset options
2. Update the question text with fresh, concise wording
3. Keep the same core question topic (${currentQuestionContext.pillar})

QUESTION FORMAT:
- Ask about the same topic in under about 15 words, without examples or introductions

PRESET FORMAT:
- Title: Brief, catchy summary (2-3 words)
- Description: One concise answer, about 6-12 words`;

      const userPrompt = `User's creative vision: "${initialPrompt}"

CURRENT QUESTION TOPIC: ${currentQuestionContext.pillar}
Question field: ${currentQuestionContext.fieldName}

PREVIOUS PRESETS (generate DIFFERENT options):
${previousPresetList}

Variation seed: ${seed}

Generate a refreshed question with new example summaries and 6 NEW preset options that are different from the previous suggestions.`;

      console.log('🔵 Preset and question refresh request');
      const startTime = Date.now();

      const object = await generateStructuredObject({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        schema: refreshSchema,
        temperature: 0.95 // High temperature for variety
      });

      const responseTime = Date.now() - startTime;
      console.log(`✅ New question and presets generated in ${responseTime}ms`);

      // Return the refreshed question with new presets
      return {
        type: 'wizard:question',
        result: {
          question: object.question,
          fieldName: currentQuestionContext.fieldName,
          pillar: currentQuestionContext.pillar,
          presets: object.presets,
          importance: 'important',
          complete: false
        },
        fieldsCount: fields.length
      };

    } else {
      // Generate next question
      const presetSchema = z.object({
        title: z.string().describe('Short 2-3 word summary title for the option (e.g., "Drinking happily", "Peek-a-boo")'),
        description: z.string().describe('Full descriptive option text (e.g., "drinking happily with a straw", "playing peek-a-boo behind it")')
      });

      const questionSchema = z.object({
        question: z.string().describe('The question to ask the user'),
        fieldName: z.string().describe('Internal field name for this data'),
        pillar: z.enum(['subject_action', 'environment_mood', 'composition_camera', 'style']).describe('Which of the four pillars this question addresses'),
        presets: z.array(presetSchema).length(6).describe('6 preset options with title summaries'),
        importance: z.enum(['critical', 'important', 'optional']).describe('How important this field is'),
        complete: z.boolean().describe('Whether we have enough information to generate images')
      });

      const fieldsCollected = fields.map(f => `${f.name}: ${f.value}`).join(', ');

      // Map collected fields to pillars
      const coveredPillars = fields.map(f => {
        const name = f.name.toLowerCase();
        if (name.includes('subject') || name.includes('action') || name.includes('activity') || name.includes('pose')) return 'subject_action';
        if (name.includes('environment') || name.includes('mood') || name.includes('atmosphere') || name.includes('setting') || name.includes('location')) return 'environment_mood';
        if (name.includes('composition') || name.includes('camera') || name.includes('framing') || name.includes('angle') || name.includes('perspective')) return 'composition_camera';
        if (name.includes('style') || name.includes('artistic') || name.includes('art') || name.includes('medium') || name.includes('aesthetic')) return 'style';
        return null;
      }).filter(Boolean);

      const normalizedPreviousPresets = Array.isArray(previousPresets)
        ? previousPresets.filter(Boolean)
        : [];

      const refreshNote = refreshOptions
        ? `\nIMPORTANT: Generate COMPLETELY DIFFERENT preset options than before.${
            normalizedPreviousPresets.length
              ? `\nPREVIOUS PRESETS:\n${normalizedPreviousPresets.map((preset, index) => `${index + 1}. ${preset}`).join('\n')}\nAvoid repeating or lightly rewording these suggestions.`
              : ''
          }
           Use creative alternatives, different styles, moods, or approaches.
           Variation seed: ${seed}\n`
        : '';

      let systemPrompt = `You are a creative assistant helping users refine their image generation ideas.
Your job is to analyze what the user has already provided and ask questions to fill in the gaps.
${refreshNote}
THE FOUR PILLARS OF A GREAT IMAGE:
1. SUBJECT & ACTION (subject_action) - Who/what is in the image and what are they doing
2. ENVIRONMENT & MOOD (environment_mood) - Where it takes place and the emotional atmosphere
3. COMPOSITION & CAMERA (composition_camera) - How it's framed (close-up, wide shot, angle, perspective)
4. STYLE (style) - Artistic medium and visual treatment (photorealistic, watercolor, anime, etc.)

PERSONALIZATION: CRITICAL - Reference the user's ACTUAL subject from their prompt in your questions. Do NOT use generic placeholders - use what THEY described.

${coveredPillars.length > 0 ? `ALREADY COVERED PILLARS: ${[...new Set(coveredPillars)].join(', ')}` : ''}

Ask about the NEXT MISSING pillar. Priority order for missing pillars:
1. subject_action (if subject/action unclear)
2. environment_mood (if setting/mood unclear)
3. composition_camera (if framing unclear)
4. style (if artistic style unclear)

QUESTION FORMAT:
- Keep questions short and clean. Do NOT include examples in the question text.
- IMPORTANT: Each question must ask about ONE SPECIFIC THING only. Never combine multiple questions into one.

PRESET FORMAT: Each preset must have a short title (2-3 words) and a longer description.
- Title: Brief, catchy summary
- Description: Full descriptive text
- CRITICAL: Presets must DIRECTLY ANSWER the question being asked. If you ask about age, presets must be age options. If you ask about an action, presets must be action options.

If all four pillars have been addressed, mark complete as true.`;

      const refreshInstruction = refreshOptions
        ? '\nThis is a REFRESH request - provide completely NEW and DIFFERENT preset options from before.\n'
        : '';

      // Format previous presets for context (handle both old string format and new object format)
      const formatPreset = (preset, index) => {
        if (typeof preset === 'object' && preset.description) {
          return `${index + 1}. ${preset.title || ''}: ${preset.description}`;
        }
        return `${index + 1}. ${preset}`;
      };
      const previousPresetContext = refreshOptions && normalizedPreviousPresets.length
        ? `Previous preset options:\n${normalizedPreviousPresets.map(formatPreset).join('\n')}\n`
        : '';

      let userPrompt = `User's initial prompt: "${initialPrompt}"

${fieldsCollected ? `Already collected fields: ${fieldsCollected}` : 'No additional details collected yet.'}
${refreshInstruction}
${previousPresetContext}
Analyze which of the four pillars are still missing or incomplete, then ask about the most important gap.
${coveredPillars.length > 0 ? `Already covered pillars: ${[...new Set(coveredPillars)].join(', ')}` : 'No pillars covered yet.'}
Generate 6 diverse preset options that cover different creative directions. Each preset needs a short title (2-3 words) and a full description.${refreshOptions ? ' Make sure these are DIFFERENT from previous suggestions.' : ''}`;

      if (stylePreference) {
        systemPrompt += `\n\nSTYLE PRIORITY: The user has a strong stylistic preference:\n${stylePreference}\nEnsure questions and presets respect and reinforce this style guidance.`;
        userPrompt += `\n\nStyle preference to emphasize: ${stylePreference}`;
      }

      console.log('🔵 Wizard question generation request');
      const startTime = Date.now();

      const object = await generateStructuredObject({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        schema: questionSchema,
        temperature: refreshOptions ? 0.95 : 0.8
      });

      const responseTime = Date.now() - startTime;
      console.log(`✅ Question generated in ${responseTime}ms: ${object.question}`);

      // Check for duplicate pillars
      const questionPillar = object.pillar;
      const isDuplicate = coveredPillars.includes(questionPillar);

      if (isDuplicate && !object.complete) {
        console.warn(`⚠️ Duplicate pillar detected: ${questionPillar} already covered`);
        console.log('   Forcing completion to avoid duplicate questions');
        object.complete = true;
      }

      // Log pillar info
      console.log(`📊 Question targets pillar: ${questionPillar}`);
      console.log(`   Covered pillars: ${[...new Set(coveredPillars)].join(', ') || 'none'}`);
      console.log(`   Remaining: ${['subject_action', 'environment_mood', 'composition_camera', 'style'].filter(p => !coveredPillars.includes(p)).join(', ') || 'all covered'}`);

      return {
        type: 'wizard:question',
        result: object,
        fieldsCount: fields.length,
        pillarStatus: {
          covered: [...new Set(coveredPillars)],
          remaining: ['subject_action', 'environment_mood', 'composition_camera', 'style'].filter(p => !coveredPillars.includes(p))
        }
      };
    }

  } catch (error) {
    console.error('Wizard error:', error);
    throw error;
  }
}
